import React, { useCallback } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Tappable } from '@vectord/components';
import { differenceWith, isEqual, orderBy } from 'lodash';

import {
  lockedSbatGroupsSelector,
  rescheduleClassesFormSelector,
  RESCHEDULE_CLASSES_STAGES,
  resetRescheduleClassesForm,
  setRescheduleClassesFormErrors,
  setRescheduleClassesStage,
  setSbatGroupsDate,
  validateRescheduleForm,
} from 'features/classCreation';
import { useGetHolidayListQuery } from 'services/holidayService';
import getDatesForSchedule from 'utils/getDatesForSchedule';

function CreateNewScheduleButton() {
  const { data } = useGetHolidayListQuery();
  const holidays = data?.data;
  const dispatch = useDispatch();
  const state = useSelector(rescheduleClassesFormSelector);
  const lockedSbatGroups = useSelector(lockedSbatGroupsSelector);

  const handleClick = useCallback(() => {
    const errors = validateRescheduleForm(state);
    if (Object.keys(errors).length > 0) {
      dispatch(setRescheduleClassesFormErrors(errors));
    } else {
      const sbatGroups = orderBy(
        Object.values(state.sbatGroups).filter((o) => !o.locked),
        'order',
        'asc'
      );

      // Retrieving all the dates from the startDate,
      // according to the selected schedule,
      // along with the total number of upcoming sbatGroups.
      const scheduleDates = getDatesForSchedule({
        holidays,
        dayTimes: state.academySchedule.dayTimes,
        start: new Date(state.startDate),
        maxDates: Object.values(state.sbatGroups).length,
      });

      // Retrieving all the dates for the sbatGroups that are locked.
      const lockedDates = lockedSbatGroups.map(
        (sbatGroup) => new Date(sbatGroup.date_of_topic)
      );

      // Remove the locked dates from the scheduled dates,
      // to get the unlocked dates.
      const unlockedDates = differenceWith(scheduleDates, lockedDates, isEqual);

      // Assign new dates to the unlocked sbatGroups
      const dates = sbatGroups.reduce((dates, sbatGroup, index) => {
        return {
          ...dates,
          [sbatGroup.id]: unlockedDates[index].getTime(),
        };
      }, {});

      batch(() => {
        dispatch(setRescheduleClassesFormErrors({}));
        dispatch(setSbatGroupsDate({ dates }));
        dispatch(
          setRescheduleClassesStage(RESCHEDULE_CLASSES_STAGES.confirmation)
        );
      });
    }
  }, [dispatch, holidays, lockedSbatGroups, state]);

  const handleGoBack = useCallback(() => {
    batch(() => {
      dispatch(setRescheduleClassesStage(RESCHEDULE_CLASSES_STAGES.view));
      dispatch(resetRescheduleClassesForm());
    });
  }, [dispatch]);

  return (
    <>
      <Tappable
        className="btn btn-primary btn-long m-r-xs"
        onClick={handleClick}
      >
        Propose New Schedule
      </Tappable>
      <Tappable
        className="btn btn-long btn-primary btn-inverted"
        onClick={handleGoBack}
      >
        Go Back
      </Tappable>
    </>
  );
}

export default CreateNewScheduleButton;
